import { SurveyListItemDto } from 'api/survey';
import {
  ClipboardList,
  Trash2,
  CheckCircle,
  XCircle,
  Calendar,
  HelpCircle
} from 'lucide-react';
import { surveyAPI } from 'services/swagger-api/surveyApi';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import { OnCreateConfig } from 'shared/contexts/table-context/components/types';
import { Column } from 'components/display/Table/types';
import { useCallback } from 'react';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import { DropdownButton, useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';

// ============================================
// TYPES & CONSTANTES
// ============================================

export const SURVEY_TABLE_PRESETS = {
  FULL: 'full',
  MINIMAL: 'minimal',
  CUSTOM: 'custom',
} as const;

export type ColumnPreset = typeof SURVEY_TABLE_PRESETS[keyof typeof SURVEY_TABLE_PRESETS];

export const SURVEY_COLUMNS = {
  NAME: 'name',
  CONTEXT: 'context',
  QUESTIONS_COUNT: 'questionsCount',
  STATUS: 'status',
  DATES: 'dates',
} as const;

export type SurveyColumnKey = typeof SURVEY_COLUMNS[keyof typeof SURVEY_COLUMNS];

export interface BaseSurveysTableProps {
  forcedFilters?: Record<string, any>;
  title?: string;
  onCreateConfig?: OnCreateConfig;
  onViewClick?: (survey: SurveyListItemDto) => void;
  onDeleteClick?: (survey: SurveyListItemDto) => void;
  onBulkDeleteClick?: (surveys: SurveyListItemDto[]) => void;
  pageSize?: number;
}

interface SurveysTableProps extends BaseSurveysTableProps {
  columnPreset?: ColumnPreset;
  customColumns?: SurveyColumnKey[];
  selectable?: boolean;
  enableFilters?: boolean;
}

// ============================================
// UTILITAIRES
// ============================================

const formatDate = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// ============================================
// DÉFINITION DES COLONNES
// ============================================

const getColumnDefinitions = (): Record<SurveyColumnKey, Column<SurveyListItemDto>> => ({
  [SURVEY_COLUMNS.NAME]: {
    key: 'name',
    label: 'Questionnaire',
    sortable: true,
    width: '300px',
    render: (_value: any, row: SurveyListItemDto) => (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <ClipboardList className="text-purple-500" size={18} />
          <span className="font-medium text-gray-900">{row.name}</span>
        </div>
        <span className="text-xs text-gray-500">
          Version {row.version}
        </span>
      </div>
    )
  },
  [SURVEY_COLUMNS.CONTEXT]: {
    key: 'contextLabel',
    label: 'Contexte',
    render: (_value: any, row: SurveyListItemDto) => (
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-900">
          {row.contextLabel}
        </span>
        {row.branchName && (
          <span className="text-xs text-gray-500">
            Branche: {row.branchName}
          </span>
        )}
      </div>
    )
  },
  [SURVEY_COLUMNS.QUESTIONS_COUNT]: {
    key: 'questionCount',
    label: 'Questions',
    align: 'center',
    render: (value: any) => (
      <div className="flex items-center justify-center gap-2">
        <HelpCircle size={14} className="text-blue-500" />
        <span className="text-sm font-medium text-gray-700">{value}</span>
      </div>
    )
  },
  [SURVEY_COLUMNS.STATUS]: {
    key: 'isActive',
    label: 'Statut',
    align: 'center',
    render: (value: any) => (
      <div className="flex justify-center">
        {value ? (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 inline-flex items-center gap-1">
            <CheckCircle size={12} />
            Actif
          </span>
        ) : (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200 inline-flex items-center gap-1">
            <XCircle size={12} />
            Inactif
          </span>
        )}
      </div>
    )
  },
  [SURVEY_COLUMNS.DATES]: {
    key: 'creationTime',
    label: 'Dates',
    sortable: true,
    render: (_value: any, row: SurveyListItemDto) => (
      <div className="flex flex-col gap-1 text-xs text-gray-600">
        <div className="flex items-center gap-2">
          <Calendar size={12} className="text-gray-400" />
          <span>Créé: {formatDate(row.creationTime)}</span>
        </div>
        {row.lastModificationTime && (
          <div className="flex items-center gap-2">
            <Calendar size={12} className="text-gray-400" />
            <span>Modifié: {formatDate(row.lastModificationTime)}</span>
          </div>
        )}
      </div>
    )
  }
});

// ============================================
// PRESETS DE COLONNES
// ============================================

const COLUMN_PRESETS: Record<ColumnPreset, SurveyColumnKey[]> = {
  [SURVEY_TABLE_PRESETS.MINIMAL]: [SURVEY_COLUMNS.NAME, SURVEY_COLUMNS.STATUS],
  [SURVEY_TABLE_PRESETS.FULL]: [
    SURVEY_COLUMNS.NAME,
    SURVEY_COLUMNS.CONTEXT,
    SURVEY_COLUMNS.QUESTIONS_COUNT,
    SURVEY_COLUMNS.STATUS,
    SURVEY_COLUMNS.DATES
  ],
  [SURVEY_TABLE_PRESETS.CUSTOM]: []
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export function GenericSurveysTable({
  forcedFilters = {},
  title,
  onViewClick,
  onCreateConfig,
  onDeleteClick,
  onBulkDeleteClick,
  columnPreset = SURVEY_TABLE_PRESETS.FULL,
  customColumns,
  selectable = true,
  enableFilters = true,
  pageSize = 15,
}: SurveysTableProps) {

  const getColumnsToDisplay = (): Column<SurveyListItemDto>[] => {
    const allColumns = getColumnDefinitions();

    if (columnPreset === SURVEY_TABLE_PRESETS.CUSTOM && customColumns) {
      return customColumns
        .map(key => allColumns[key])
        .filter((col): col is Column<SurveyListItemDto> => col !== undefined);
    }

    const columnKeys = COLUMN_PRESETS[columnPreset];
    return columnKeys
      .map(key => allColumns[key])
      .filter((col): col is Column<SurveyListItemDto> => col !== undefined);
  };

  const fetchSurveys = async (params: any) => {
    const response = await surveyAPI.getSurveys({
      ...forcedFilters,
      ...params.filters
    });

    const items = response.items || [];

    // Filtrage côté client pour la recherche (si l'API ne le supporte pas)
    let filteredItems = items;
    if (params.searchString) {
      const searchLower = params.searchString.toLowerCase();
      filteredItems = items.filter(item =>
        item.name.toLowerCase().includes(searchLower) ||
        item.contextLabel.toLowerCase().includes(searchLower)
      );
    }

    // Pagination côté client
    const startIndex = (params.pageNumber - 1) * params.pageSize;
    const paginatedItems = filteredItems.slice(startIndex, startIndex + params.pageSize);

    return {
      items: paginatedItems,
      totalItems: filteredItems.length,
      totalPages: Math.ceil(filteredItems.length / params.pageSize),
      currentPage: params.pageNumber
    };
  };

  const fetchFilters = async () => {
    return [
      {
        groupName: 'Statut',
        filters: [
          {
            key: 'activeOnly',
            label: 'Actifs uniquement',
            type: 'select' as const,
            options: [{ value: 'true', label: 'Actifs uniquement' }]
          }
        ]
      }
    ];
  };

  const handleView = (survey: SurveyListItemDto) => {
    onViewClick ? onViewClick(survey) : console.log('Visualiser le questionnaire:', survey);
  };

  const handleDelete = useCallback((survey: SurveyListItemDto) => {
    onDeleteClick ? onDeleteClick(survey) : console.log('Supprimer:', survey);
  }, [onDeleteClick]);

  const handleBulkDelete = (surveys: SurveyListItemDto[]) => {
    onBulkDeleteClick ? onBulkDeleteClick(surveys) : console.log('Suppression groupée de', surveys.length, 'questionnaires');
  };

  const { createMenuItem } = useActionMenu();

  const createMenuItems = useCallback((survey: SurveyListItemDto) => [
    createMenuItem(
      'delete',
      'Supprimer',
      () => handleDelete(survey),
      { icon: 'trash', variant: 'danger' },
    ),
  ], [createMenuItem, handleDelete]);

  return (
    <ServerTable<SurveyListItemDto>
      apiConfig={{
        fetchData: fetchSurveys,
        fetchFilters: enableFilters ? fetchFilters : undefined
      }}

      columns={getColumnsToDisplay()}

      title={title ? {
        label: title,
        iconKey: 'clipboard-list'
      } : undefined}

      actions={{
        create: onCreateConfig,
        row: [
          {
            label: 'seeMore',
            render: (survey: SurveyListItemDto, index: number) => (
              <Button key={index} color='slate' round onClick={() => handleView(survey)}>
                <EyeIcon />
              </Button>
            ),
          },
          {
            label: 'moreMenu',
            render: (survey: SurveyListItemDto, index: number) => (
              <DropdownButton
                key={index}
                width='w-56'
                anchor='bottom'
                items={createMenuItems(survey)}
              />
            ),
          },
        ],
        bulk: selectable ? [
          {
            label: 'Supprimer la sélection',
            icon: <Trash2 size={16} />,
            onClick: handleBulkDelete,
            variant: 'danger'
          }
        ] : undefined
      }}

      selectable={selectable}
      pageSize={pageSize}
      pageSizeOptions={[10, 15, 20, 30, 50]}
      showPaginationInfo

      enableSearch
      enableFilters={enableFilters}
      searchPlaceholder="Rechercher un questionnaire..."

      skeletonRowCount={10}

      onRowClick={handleView}
    />
  );
}
